import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import { FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import type { RecommendationItem } from 'voyager';
import { Recommendations, SpaDayDetailTemplate } from 'voyager';

import { BasketDrawerConnected } from '@common/components/BasketDrawer';
import { Routes } from '@common/constants';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { useGetSpaDaysPage, useGetSpaPackage, useGetSpaSettings } from '@features/spa/hooks';
import { useGetSpaPackageExtras } from '@features/spa/hooks/useGetSpaPackageExtras';
import { useTranslations } from '@lang';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { mergeCrossSellWithUpsell, parseDateToString } from '@utils/helpers';
import { SpaPackageType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues, buildSpaPackageFormSchema } from '../SpaPackageForm.schema';
import { SpaDayBookingV2 } from './SpaDayBookingV2';
import { SpaDayExtrasV2 } from './SpaDayExtrasV2';
import { SpaDayV2Props } from './SpaDayV2.types';

type CrossSellObject = {
  id: number | string;
  name?: string;
  image?: { url?: string };
  price?: { amount: number; currencyCode: string } | null;
  durationText?: string;
};

function mapCrossSellToRecommendations(
  crossSellItems: Array<{ type: string; object: CrossSellObject }>,
  fallbackImageUrl?: string
): RecommendationItem[] {
  return crossSellItems
    .filter(({ object }) => !!object?.name)
    .map(({ object }) => ({
      id: String(object.id),
      name: object.name!,
      imageUrl: object.image?.url || fallbackImageUrl,
      price: object.price ?? null,
      meta: object.durationText,
    }));
}

export const SpaDayV2: FC<SpaDayV2Props> = ({ id, property, language, bookingStep }) => {
  const router = useRouter();
  const { t } = useTranslations();
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams);
  const startDate = moment(journeyParams?.spaDate).format('YYYY-MM-DD');

  const journeyBar = <JourneyPickerV2Connected dateMode="single" dateContext="spa" showGuests />;

  const { data: spaPackage, isFetching } = useGetSpaPackage(property.id, id, language);
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const { data: spaDaysPage } = useGetSpaDaysPage(property.id, language);
  const fallbackImageUrl = spaDaysPage?.heroBackgroundImage?.url;

  const entity = spaPackage as SpaPackageType | undefined;

  const selectedDateString = moment(journeyParams.spaDate).format('YYYY-MM-DD');

  const { data: extras = [] } = useGetSpaPackageExtras(
    property.id,
    id,
    language,
    selectedDateString
  );

  const form = useForm<SpaPackageFormValues>({
    resolver: entity ? zodResolver(buildSpaPackageFormSchema(entity)) : undefined,
    defaultValues: {
      date: selectedDateString,
      guests: entity
        ? Array.from({ length: entity.numberOfGuests }, () => ({
            title: '',
            firstName: '',
            lastName: '',
            gender: '',
          }))
        : [{ title: '', firstName: '', lastName: '', gender: '' }],
    },
    mode: 'onChange',
  });

  const handleCloseModal = useCallback(
    (showMiniBasket = false) => {
      router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id } });
      if (showMiniBasket) {
        setIsBasketDrawerOpen(true);
      }
    },
    [router, id]
  );

  const handleReserve = useCallback(() => {
    router.push({
      pathname: Routes.SPA_DAY_BOOKING,
      query: { id, bookingStep: 'availability' },
    });
  }, [router, id]);

  const handleSelectRecommendation = useCallback(
    (recId: string) => {
      router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id: recId } });
    },
    [router]
  );

  const handleBack = useCallback(() => {
    router.push({
      pathname: Routes.SPA_DAY_LIST,
      query: { arrivalDate: parseDateToString(journeyParams.spaDate) },
    });
  }, [router]);

  const crossSellItems = mergeCrossSellWithUpsell(
    spaPackage?.upSell ?? null,
    'spa-day',
    spaPackage?.crossSellObjects ?? null
  );

  const recommendationItems = mapCrossSellToRecommendations(crossSellItems, fallbackImageUrl);

  const recommendations =
    recommendationItems.length > 0 ? (
      <Recommendations
        items={recommendationItems}
        onSelect={handleSelectRecommendation}
        title={t('product.otherProducts.title')}
        fromPriceLabel={t('common.from')}
      />
    ) : undefined;

  const termsContent =
    spaPackage?.termsAndConditions || spaSettings?.termsAndConditions ? (
      <>
        {spaPackage?.termsAndConditions && <p>{spaPackage.termsAndConditions}</p>}
        {spaSettings?.termsAndConditions && <p>{spaSettings.termsAndConditions}</p>}
      </>
    ) : undefined;

  const propertyModules = Object.keys(property.modulesExcludingDisabled);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  return (
    <>
      {!bookingStep && (
        <SpaDayDetailTemplate
          imageUrl={spaPackage?.image?.url ?? null}
          imageAlt={spaPackage?.name}
          title={spaPackage?.name ?? ''}
          price={spaPackage?.price}
          priceLabel={t('common.from')}
          categoryLabel={spaPackage?.categories?.[0]?.name}
          description={spaPackage?.description}
          termsContent={termsContent}
          termsLabel={t('homepage.termsAndConditions')}
          onReserve={handleReserve}
          reserveLabel={t('spa.book')}
          onBack={handleBack}
          recommendations={recommendations}
          isLoading={isFetching}
          journeyBar={journeyBar}
        />
      )}
      {bookingStep === 'availability' && entity && (
        <FormProvider {...form}>
          <SpaDayBookingV2
            id={id}
            property={property}
            language={language}
            startDate={startDate}
            onClose={handleCloseModal}
          />
        </FormProvider>
      )}
      {bookingStep === 'extras' && entity && (
        <FormProvider {...form}>
          <SpaDayExtrasV2
            extras={extras}
            entity={entity}
            property={property}
            language={language}
            id={id}
            onClose={handleCloseModal}
            onBack={() =>
              router.push({
                pathname: Routes.SPA_DAY_BOOKING,
                query: { id, bookingStep: 'availability' },
              })
            }
          />
        </FormProvider>
      )}
      <BasketDrawerConnected
        open={isBasketDrawerOpen}
        onClose={() => setIsBasketDrawerOpen(false)}
        propertyModules={propertyModules}
        hideArrivalTimes={hideArrivalTimes}
      />
    </>
  );
};
